import { useState } from 'react';
import type { Notification } from '../types';

interface NotificationTableProps {
  notifications: Notification[];
  onEdit: (notification: Notification) => void;
  onDelete: (id: string) => void;
}

export default function NotificationTable({ notifications, onEdit, onDelete }: NotificationTableProps) {
  const getTypeText = (type: string) => {
    return type === 'activity' ? '活动通知' : '平台通知';
  };

  const getTargetText = (target: string) => {
    const map: Record<string, string> = {
      'all-users': '全体用户',
      'all-schools': '全体学校（管理员）',
      'all-enterprises': '全体企业（管理员）'
    };
    return map[target] || target;
  };

  const getStatusText = (status: string) => {
    return status === 'sent' ? '已通知' : '未通知';
  };

  const truncateContent = (content: string, maxLength: number = 100) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">通知标题</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">通知类型</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">通知内容</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">通知对象</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">通知状态</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">通知时间</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">创建时间</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">创建人</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {notifications.map((notification) => (
              <tr key={notification.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-900">{notification.title}</td>
                <td className="px-4 py-3">
                  <span className={`px-2.5 py-1 text-xs rounded-full whitespace-nowrap ${
                    notification.type === 'activity'
                      ? 'bg-orange-50 text-orange-600'
                      : 'bg-blue-50 text-blue-600'
                  }`}>
                    {getTypeText(notification.type)}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 max-w-md">
                  {truncateContent(notification.content)}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                  {getTargetText(notification.target)}
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2.5 py-1 text-xs rounded-full whitespace-nowrap ${
                    notification.status === 'sent'
                      ? 'bg-green-50 text-green-600'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {getStatusText(notification.status)}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{notification.notificationTime}</td>
                <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{notification.createTime}</td>
                <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{notification.creator}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onEdit(notification)}
                      className="w-7 h-7 flex items-center justify-center text-teal-600 hover:bg-teal-50 rounded cursor-pointer"
                      title="编辑"
                    >
                      <i className="ri-edit-line text-sm"></i>
                    </button>
                    <button
                      onClick={() => onDelete(notification.id)}
                      className="w-7 h-7 flex items-center justify-center text-red-600 hover:bg-red-50 rounded cursor-pointer"
                      title="删除"
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
